import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import { DateTime } from "luxon";
import { UseCases } from "@planzone/core";
import { Assignable, project as projectMock, Removable } from "@planzone/dto";
import {
  useAssignTag,
  useListEdition,
  useProjectEdition,
} from "@planzone/view-models";
import { ProjectStatus } from "@scenes/project/projectStatus";
import { Safeguard } from "@scenes/safeguard";
import { useModal } from "@root/client/modal";
import { ActivityWatcher } from "@common/activityWatcher";
import { FolderIcon } from "@common/folderIcon";
import { ListToggleArrow } from "@common/listToggleArrow";
import { SecureContextualMenu } from "@common/secureContextualMenu";
import { ZoneAvatar } from "@common/zoneAvatar";
import { useToggle } from "@common/treeToggle";
import { Context } from "../context";
import { SecondaryCriteriaForm } from "@common/secondaryCriteriaForm";
import { Tags } from "../tags";
import { CommentsCount } from "../commentsCount";
import { AttachmentsCount } from "../attachmentsCount";
import { SecondaryCriteria } from "../secondaryCriteria";
import { AllocationUnit } from "@common/allocationUnit";
import { DateForm } from "../dateForm";
import { useProjectCtx } from "../projectCtx";
import { Props } from "./types";
import styles from "./styles.scss";

import { useIsElementHovered } from "@common/elementHover";

function toFixed(nbr: number) {
  return typeof nbr === "number" || !isNaN(parseFloat(nbr))
    ? Math.round(nbr) === nbr
      ? nbr
      : nbr.toFixed(2)
    : "";
}

function Project({ project, isMultiProject, meta, filter }: Props) {
  const { t } = useTranslation();
  const { isOpen, toggle } = useToggle(
    project.uuid,
    project.depth,
    !isMultiProject,
  );
  const isHovered = useIsElementHovered(project.uuid);
  const ref = React.useRef<HTMLDivElement | null>(null);
  const { template, project: p } = useProjectCtx();
  const projectTimezone = p.timeZone || DateTime.local().zoneName;
  const { pushView } = useModal();
  const { assignTag } = useAssignTag();
  const { editProject } = useProjectEdition(p);
  const { isRequestPending, editList } = useListEdition();
  const [showContextualMenu, setShowContextualMenu] = useState(false);

  ActivityWatcher.useActivityWatcher(ref, {
    focus: () => setShowContextualMenu(!showContextualMenu),
    blur: () => setShowContextualMenu(false),
  });

  return (
    <div
      className={classNames(styles.project, {
        [styles.hovered]: isHovered,
      })}
      ref={ref}
    >
      <SecondaryCriteria project={p}>
        {() => ({
          main: (
            <div className={styles.main}>
              <div className={styles.wrapper}>
                <div className={styles.toggleArrowBox} onClick={toggle}>
                  <ListToggleArrow isOpen={isOpen} />
                </div>
                {filter.scope === "planzone" && (
                  <div className={styles.zone} onClick={toggle}>
                    <ZoneAvatar {...project.zone} />
                  </div>
                )}
                <div onClick={toggle}>
                  <FolderIcon className={styles.icon} isOpen={isOpen} />
                </div>
                <Safeguard useCases={[UseCases.editProject]}>
                  {({ canEditProject }) => (
                    <span
                      onClick={() => {
                        canEditProject &&
                          pushView({
                            key: UseCases.retrieveProject,
                            data: {
                              project: { ...projectMock, ...project },
                              isMultiProject,
                            },
                          });
                      }}
                      className={styles.name}
                    >
                      {project.name}
                    </span>
                  )}
                </Safeguard>
              </div>
            </div>
          ),
          criteria: template.reduce(
            (acc, criteria) => ({
              ...acc,
              [criteria.uuid]: (
                <SecondaryCriteriaForm
                  criteria={criteria}
                  value={
                    project.attributes
                      ? project.attributes[criteria.uuid] || ""
                      : ""
                  }
                  projectTimezone={projectTimezone}
                  disabled={isRequestPending}
                  submit={(value) =>
                    editProject({
                      project: p,
                      nextProject: {
                        ...p,
                        attributes: {
                          ...project.attributes,
                          [criteria.uuid]: value,
                        },
                      },
                    })
                  }
                />
              ),
            }),
            {
              statuses: (
                <div className={styles.status}>
                  <ProjectStatus
                    project={p}
                    selectStatus={(status) =>
                      editProject({
                        project: p,
                        nextProject: { ...p, status },
                      })
                    }
                    isMinimized
                  />
                </div>
              ),
              tags: (
                <p className={styles.tags}>
                  <Tags
                    tags={project.tags}
                    project={p}
                    removable={Removable.PROJECT}
                    uuid={project.uuid}
                    add={(tag) =>
                      assignTag({
                        tag,
                        assignable: Assignable.PROJECT,
                        assignableId: project.uuid,
                        project: p,
                      })
                    }
                  />
                </p>
              ),
              allocated: (
                <div className={styles.allocated}>
                  {toFixed(meta.allocatedHours)}
                  <AllocationUnit
                    amount={meta.allocatedHours}
                    format="symbol"
                  />
                </div>
              ),
              spent: (
                <div className={styles.allocated}>
                  {toFixed(meta.spentHours)}
                  <AllocationUnit amount={meta.spentHours} format="symbol" />
                </div>
              ),
              startDate: (
                <div className={styles.endDate}>
                  <DateForm
                    projectElement={project}
                    projectTimezone={projectTimezone}
                    date={p.rootList.startDate}
                    disabled={isRequestPending}
                    submit={(date) => {
                      const nextDt = DateTime.fromISO(date, { zone: "utc" });
                      const nextIso = nextDt.toUTC().toISO() ?? "";
                      const currentDt = p.rootList.startDate
                        ? DateTime.fromISO(p.rootList.startDate, {
                            zone: "utc",
                          })
                        : null;
                      editList({
                        list: p.rootList,
                        nextList: {
                          ...p.rootList,
                          startDate:
                            !!p.rootList.startDate &&
                            !!currentDt &&
                            nextDt.equals(currentDt)
                              ? ""
                              : nextIso,
                        },
                      });
                    }}
                  />
                </div>
              ),
              endDate: (
                <div className={styles.endDate}>
                  <DateForm
                    projectElement={project}
                    projectTimezone={projectTimezone}
                    date={p.rootList.endDate}
                    recalculatedDate=""
                    disabled={isRequestPending}
                    submit={(date) => {
                      const nextDt = DateTime.fromISO(date, { zone: "utc" });
                      const nextIso = nextDt.toUTC().toISO() ?? "";
                      const currentDt = p.rootList.endDate
                        ? DateTime.fromISO(p.rootList.endDate, {
                            zone: "utc",
                          })
                        : null;
                      editList({
                        list: p.rootList,
                        nextList: {
                          ...p.rootList,
                          endDate:
                            !!p.rootList.endDate &&
                            !!currentDt &&
                            nextDt.equals(currentDt)
                              ? ""
                              : nextIso,
                        },
                      });
                    }}
                  />
                </div>
              ),
            },
          ),
          comments: (
            <CommentsCount
              subject={p.uuid}
              type="list"
              commentsNbr={meta.commentCount}
            />
          ),
          files: (
            <AttachmentsCount
              uuid={project.uuid}
              fileNbr={meta.attachmentCount}
            />
          ),
          contextualMenu: (
            <div
              ref={ref}
              className={classNames(styles.contextualMenu, {
                [styles.displayedContextualMenu]: showContextualMenu,
              })}
            >
              <SecureContextualMenu
                items={[
                  {
                    name: t("projectSection.contextualMenu.projectDetails"),
                    useCase: UseCases.editProject,
                    act: () =>
                      pushView({
                        data: { project: p },
                        key: UseCases.retrieveProject,
                      }),
                  },
                  {
                    name: t("projectSection.contextualMenu.createProjectModel"),
                    useCase: UseCases.createProjectModelFromProject,
                    act: () =>
                      pushView({
                        data: p,
                        key: UseCases.createProjectModelFromProject,
                      }),
                  },
                  {
                    name: t("projectSection.contextualMenu.projectStatus"),
                    useCase: UseCases.editProject,
                    act: () =>
                      pushView({
                        data: p,
                        key: UseCases.retrieveTaskStatuses,
                      }),
                  },
                  {
                    name: t("projectSection.contextualMenu.strategicImpact"),
                    useCase: UseCases.createProjectStrategicAxis,
                    act: () =>
                      pushView({
                        data: p,
                        key: UseCases.createProjectStrategicAxis,
                      }),
                  },
                  {
                    name: t("projectSection.contextualMenu.remove"),
                    useCase: UseCases.removeProject,
                    color: "#DB5F63FF",
                    act: () =>
                      pushView({
                        data: p,
                        key: UseCases.removeProject,
                      }),
                  },
                ]}
              />
            </div>
          ),
        })}
      </SecondaryCriteria>
    </div>
  );
}

export { Project };
